import type {
  AppUserRole,
  BotConfig,
  BotState,
  BotToken,
  BotTokenCreate,
  BotTokenListItem,
  CreatedUserResponse,
  DsConfig,
  FeedbackResponse,
  SaveConfigResponse,
  StudentsListResponse,
} from "@/types/api";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorData.message || errorMessage;
    } catch {
      // ignore JSON parse errors
    }
    throw new ApiError(response.status, response.statusText, errorMessage);
  }

  // Handle empty responses
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return {} as T;
  }

  return response.json();
}

class ApiClient {
  async changeMyPassword(newPassword: string): Promise<void> {
    await this.put("/api/users/me/password", { new_password: newPassword });
  }

  async changeUserPassword(username: string, newPassword: string): Promise<void> {
    await this.put(`/api/users/${encodeURIComponent(username)}/password`, {
      new_password: newPassword,
    });
  }

  // Bot Tokens API
  async createBotToken(data: BotTokenCreate): Promise<BotToken> {
    return this.fetch<BotToken>("/api/bot-tokens", {
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });
  }

  async createConfig(data: FormData): Promise<SaveConfigResponse> {
    return this.fetch<SaveConfigResponse>("/api/configs", {
      body: data,
      method: "POST",
    });
  }

  async createUser(body: {
    password: string;
    role: AppUserRole;
    username: string;
  }): Promise<CreatedUserResponse> {
    return this.post<CreatedUserResponse>("/api/users", body);
  }

  async delete<T>(url: string): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      credentials: "include",
      headers,
      method: "DELETE",
    });

    if (response.status === 401) {
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      throw new Error("Unauthorized");
    }

    return handleResponse<T>(response);
  }

  async deleteBotToken(tokenId: number): Promise<void> {
    return this.fetch<void>(`/api/bot-tokens/${tokenId}`, {
      method: "DELETE",
    });
  }

  async deleteConfig(botId: string): Promise<void> {
    return this.fetch<void>(`/api/configs/${encodeURIComponent(botId)}`, {
      method: "DELETE",
    });
  }

  /** Простой аналитический отчёт по всем ботам */
  async exportAnalyticsReport(payload: { dateFrom: string; dateTo: string }): Promise<void> {
    const params = new URLSearchParams({
      date_from: payload.dateFrom,
      date_to: payload.dateTo,
    });
    await this.downloadReportBlob(`/api/reports/analytics?${params}`, "analytics_report.xlsx");
  }

  /** Расширенный аналитический отчёт по сценарию */
  async exportExtendedAnalyticsReport(payload: {
    dateFrom: string;
    dateTo: string;
    includeTranscript?: boolean;
    scenarioId: number;
  }): Promise<void> {
    const params = new URLSearchParams({
      date_from: payload.dateFrom,
      date_to: payload.dateTo,
      include_transcript: String(payload.includeTranscript ?? false),
      scenario_id: String(payload.scenarioId),
    });
    await this.downloadReportBlob(
      `/api/reports/analytics/extended?${params}`,
      "analytics_report_extended.xlsx"
    );
  }

  async exportUserConversations(
    userId: number,
    options?: { botId?: string; format?: "csv" | "xlsx"; platform?: string }
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options?.format) params.set("format", options.format);
    if (options?.botId) params.set("bot_id", options.botId);
    if (options?.platform) params.set("platform", options.platform);
    const query = params.toString();
    const url = `/api/conversations/${userId}/export${query ? `?${query}` : ""}`;
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}${url}`, {
      credentials: "include",
      headers,
    });

    if (response.status === 401) {
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Unknown error" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = `conversations_user_${userId}.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  // Generic HTTP methods
  async get<T>(url: string): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      credentials: "include",
      headers,
      method: "GET",
    });

    if (response.status === 401) {
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      const error = new ApiError(401, "Unauthorized", "Unauthorized");
      throw error;
    }

    if (!response.ok) {
      let errorMessage = response.statusText;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        // ignore JSON parse errors
      }
      throw new ApiError(response.status, response.statusText, errorMessage);
    }

    return handleResponse<T>(response);
  }

  async getBlob(url: string): Promise<Response> {
    const token = this.getToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      credentials: "include",
      headers,
      method: "GET",
    });

    if (response.status === 401) {
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      const error = new ApiError(401, "Unauthorized", "Unauthorized");
      throw error;
    }

    return response;
  }

  // Bot Management API
  async getBotStates(): Promise<BotState[]> {
    return this.fetch<BotState[]>("/api/bots/states");
  }

  async getConfig(botId: string): Promise<BotConfig> {
    return this.fetch<BotConfig>(`/api/configs/${encodeURIComponent(botId)}`);
  }

  // Config API
  async getConfigs(): Promise<BotConfig[]> {
    return this.fetch<BotConfig[]>("/api/configs");
  }

  // DS Config methods
  async getDsConfig(botId: string): Promise<DsConfig> {
    return this.fetch<DsConfig>(`/api/configs/${encodeURIComponent(botId)}/ds`);
  }

  async getFeedback(options?: {
    cursor?: string;
    pageSize?: number;
    scenarioId?: number;
  }): Promise<FeedbackResponse> {
    const params = new URLSearchParams();
    if (options?.scenarioId) params.set("scenario_id", String(options.scenarioId));
    if (options?.cursor) params.set("cursor", options.cursor);
    if (options?.pageSize !== undefined) params.set("page_size", String(options.pageSize));
    const query = params.toString();
    return this.fetch<FeedbackResponse>(`/api/feedback${query ? `?${query}` : ""}`);
  }

  // Reports API
  async getStudents(options?: {
    botId?: string;
    isActive?: boolean;
  }): Promise<StudentsListResponse> {
    const params = new URLSearchParams();
    if (options?.isActive !== undefined) params.set("is_active", String(options.isActive));
    if (options?.botId) params.set("bot_id", options.botId);
    const query = params.toString();
    return this.fetch<StudentsListResponse>(`/api/users/students${query ? `?${query}` : ""}`);
  }

  async listAvailableBotTokens(): Promise<BotTokenListItem[]> {
    return this.fetch<BotTokenListItem[]>("/api/bot-tokens");
  }

  // Auth API
  async login(
    username: string,
    password: string,
    next?: string,
    options?: { redirect?: boolean }
  ): Promise<void> {
    const formData = new URLSearchParams();
    formData.append("username", username);
    formData.append("password", password);

    const response = await fetch(`${API_BASE}/api/auth/login`, {
      body: formData,
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Неверные учётные данные" }));
      throw new Error(error.detail || "Неверные учётные данные");
    }

    const data = await response.json();

    // Сохраняем JWT токен (бессрочный)
    if (data.access_token) {
      this.setToken(data.access_token);
    }

    // Successful login
    if (options?.redirect ?? true) {
      window.location.href = next || "/";
    }
  }

  async logout(): Promise<void> {
    // Очищаем JWT токен и редиректим на логин
    this.clearToken();
    window.location.href = "/login";
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
      headers,
      method: "POST",
    });

    if (response.status === 401) {
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      throw new Error("Unauthorized");
    }

    return handleResponse<T>(response);
  }

  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      body: formData,
      credentials: "include",
      headers,
      method: "POST",
    });

    if (response.status === 401) {
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      throw new Error("Unauthorized");
    }

    return handleResponse<T>(response);
  }

  async put<T>(url: string, data: unknown): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      body: JSON.stringify(data),
      credentials: "include",
      headers,
      method: "PUT",
    });

    if (response.status === 401) {
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      throw new Error("Unauthorized");
    }

    return handleResponse<T>(response);
  }

  async restartBot(botId: string): Promise<void> {
    return this.fetch<void>(`/api/bots/${encodeURIComponent(botId)}/restart`, {
      method: "POST",
    });
  }

  async startBot(botId: string): Promise<void> {
    return this.fetch<void>(`/api/bots/${encodeURIComponent(botId)}/start`, {
      method: "POST",
    });
  }

  async stopBot(botId: string): Promise<void> {
    return this.fetch<void>(`/api/bots/${encodeURIComponent(botId)}/stop`, {
      method: "POST",
    });
  }

  async updateConfig(botId: string, data: FormData): Promise<SaveConfigResponse> {
    return this.fetch<SaveConfigResponse>(`/api/configs/${encodeURIComponent(botId)}`, {
      body: data,
      method: "PUT",
    });
  }

  async updateDsConfig(botId: string, config: DsConfig): Promise<DsConfig> {
    return this.fetch<DsConfig>(`/api/configs/${encodeURIComponent(botId)}/ds`, {
      body: JSON.stringify(config),
      headers: { "Content-Type": "application/json" },
      method: "PUT",
    });
  }

  private clearToken(): void {
    localStorage.removeItem("jwt_token");
  }

  /** Скачивает бинарный отчёт по GET и сохраняет файл */
  private async downloadReportBlob(url: string, defaultFilename: string): Promise<void> {
    const response = await this.getBlob(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Unknown error" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = defaultFilename;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private async fetch<T>(url: string, options?: RequestInit): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      ...(options?.headers as Record<string, string>),
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      credentials: "include",
      headers,
    });

    if (response.status === 401) {
      // Clear token and redirect to login if unauthorized
      this.clearToken();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Unknown error" }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  private getToken(): null | string {
    return localStorage.getItem("jwt_token");
  }

  private setToken(token: string): void {
    localStorage.setItem("jwt_token", token);
  }
}

export const apiClient = new ApiClient();
export { ApiError };
